<template lang="pug">
md-layout(md-column='')

  // Page loader
  md-progress.md-accent(md-indeterminate='', v-show='loadingComponent')
  // Page content
  md-theme(md-name='blue', v-show='!loadingComponent')
    // Title
    breadcrumbs(section='Accounts', :nav='nav')

    // Form
    md-layout.form-wrapper(:md-gutter='16')
      md-whiteframe.form-wrapper-frame(md-elevation='3')
        .md-subheading Partner Account
        md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.account.name.$invalid }')
          label Name
          md-input(v-model='account.name', :disabled="isAccountAdmin()")
          span.md-error(v-if='formSubmitted && !$v.account.name.required') This field is required
          span.md-error(v-if='formSubmitted && $v.account.name.required && !$v.account.name.isUnique && !$v.account.name.$pending') Must be unique

      md-whiteframe.form-wrapper-frame(md-elevation='3')
        .md-subheading Billing Contact
        md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.account.billing_contact.first_name.$invalid }')
          label First Name
          md-input(v-model='account.billing_contact.first_name')
          span.md-error(v-if='formSubmitted && !$v.account.billing_contact.first_name.required') This field is required

        md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.account.billing_contact.last_name.$invalid }')
          label Last Name
          md-input(v-model='account.billing_contact.last_name')
          span.md-error(v-if='formSubmitted && !$v.account.billing_contact.last_name.required') This field is required

        md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.account.billing_contact.title.$invalid }')
          label Title
          md-input(v-model='account.billing_contact.title')
          span.md-error(v-if='formSubmitted && !$v.account.billing_contact.title.required') This field is required

        md-input-container(:class='{ "md-input-invalid" : formSubmitted && $v.account.billing_contact.email.$invalid }')
          label Email
          md-input(v-model='account.billing_contact.email')
          span.md-error(v-if='formSubmitted && !$v.account.billing_contact.email.required') This field is required

      md-layout(md-align='center')
        stitch-loader(:loading='creatingOrUpdating', :size='40')
        md-button#submit-btn.md-raised.md-primary(@click='onFormSubmit', v-show='!creatingOrUpdating', :disabled="$v.$pending", md-theme='blue')
          | {{ editMode ? 'Update' : 'Create' }}
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import StitchLoader from '../../shared-components/Loader';
  import MetadataForm from '../../shared-components/forms/MetadataForm';
  import accountService from '../../services/accountService';
  import StatusRadio from '../../shared-components/StatusRadio';
  import NetworkSelector from '../../shared-components/network/NetworkSelector';
  import Breadcrumbs from '../../shared-components/Breadcrumbs';

  export default {
    mixins: [validationMixin],
    data() {
      return {
        accountOriginalName: null,
        loadingComponent: false,
        creatingOrUpdating: false,
        formSubmitted: false,
        account: {
          name: '',
          billing_contact: {
            first_name: '',
            last_name: '',
            title: '',
            email: ''
          },
        }
      };
    },
    validations: {
      account: {
        name: {
          required,
          async isUnique(value) {
            const hasToCheck = !this.editMode || value !== this.accountOriginalName;
            if (value && hasToCheck) {
              const response = await accountService.getList({ exactName: value, limit: 1 });
              return response.data.accounts.length === 0;
            } else {
              return true;
            }
          }
        },
        billing_contact: {
          first_name: {
            required
          },
          last_name: {
            required
          },
          title: {
            required
          },
          email: {
            required
          }
        }
      }
    },
    components: {
      StitchLoader,
      StatusRadio,
      MetadataForm,
      NetworkSelector,
      Breadcrumbs
    },
    computed: {
      ...mapState({
        siteuser: state => state.user.siteuser
      }),
      nav() {
        return [
          { title: this.isAccountAdmin() ? "Account" : "Accounts", route: 'AccountList' },
          { title: this.editMode ? 'Edit Account' : 'New Account' }
        ]
      },
      editMode() {
        return !!this.$route.params.id;
      }
    },
    methods: {
      showAlertToast(text, icon){
        this.$swal({
              text: text,
              position: 'bottom-left',
              icon: icon,
              toast: true,
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false,
              }
            )
      },
      isAccountAdmin() {
        // when user is of role AccountAdmin, don't call getList() and hide md-select, show just account name
        if (this.siteuser.authorization && this.siteuser.authorization.roles) {
          if(this.siteuser.authorization.roles.includes('AccountAdmin')) {
            return true;
          }
        }

        return false;
      },
      createAccount() {
        this.creatingOrUpdating = true;
        accountService.create(this.account)
          .then(() => {
            this.showAlertToast('Account created successfully', 'success');
            this.$delayedNavigation(this.$router, 'AccountList');
          })
          .catch(() => {
            this.showAlertToast('Account could not be created. Please, try again later.', 'error');
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },
      updateAccount() {
        this.creatingOrUpdating = true;
        accountService.update(this.$route.params.id, this.account)
          .then(() => {
            this.showAlertToast('Account updated successfully', 'success');
          })
          .catch(() => {
            this.showAlertToast('Account could not be updated. Please, try again later.', 'error');
          })
          .then(() => {
            this.creatingOrUpdating = false;
          });
      },
      onFormSubmit() {
        this.formSubmitted = true;
        if (!this.$v.$invalid) {
          if (this.editMode) {
            this.updateAccount();
          } else {
            this.createAccount();
          }
        }
      }
    },
    mounted() {
      if (this.editMode) {
        this.loadingComponent = true;
        accountService.get(this.$route.params.id)
          .then((response) => {
            this.account = response.data.account;
            this.accountOriginalName = this.account.name;
          })
          .catch(() => {
            this.showAlertToast('Account data could not be retrieved. Please, try again later.', 'error');
          })
          .then(() => {
            this.loadingComponent = false;
          });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .form-wrapper {
    width: 100%;
    padding: 25px 25%;
    .form-wrapper-frame {
      width: 100%;
      padding: 25px;
      margin-bottom: 15px;
      .md-subheading {
        margin-top: 35px;
      }
    }
    @media (max-width: 1200px) {
      padding: 10px .5%;
      margin-left: 0;
    }
  }
  .title-icon {
    margin-top: -5px;
  }
  #submit-btn {
    margin: 25px 0;
    width: 33%;
    padding: 6px;
    font-size: 1.1em;
  }
</style>



// WEBPACK FOOTER //
// src/spa/account/AccountForm.vue